import React, { useEffect, useState } from 'react';
import Layout from '../../components/layout/Layout';
import api from '../../services/api';
import { UserPlus, Search, CheckCircle, XCircle, Download } from 'lucide-react';
import toast from 'react-hot-toast';
import { exportUsersReport } from '../../utils/pdfExport';

const Users = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [roleFilter, setRoleFilter] = useState('');
  const [showAddModal, setShowAddModal] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [editData, setEditData] = useState({});
  const [newUser, setNewUser] = useState({
    full_name: '', email: '', phone_number: '',
    password: '', role: '', district_id: '',
    cnic: '', employee_id: ''
  });
  const [districts, setDistricts] = useState([]);

  const fetchUsers = async () => {
    try {
      setLoading(true);
      const params = {};
      if (search) params.search = search;
      if (roleFilter) params.role = roleFilter;
      const response = await api.get('/users', { params });
      setUsers(response.data.users);
    } catch (err) {
      toast.error('Failed to load users');
    } finally {
      setLoading(false);
    }
  };

  const fetchDistricts = async () => {
    try {
      const response = await api.get('/districts');
      setDistricts(response.data.districts);
    } catch (err) {
      console.error('Failed to load districts');
    }
  };

  useEffect(() => {
    fetchUsers();
    fetchDistricts();
  }, []);

  useEffect(() => {
    fetchUsers();
  }, [search, roleFilter]);

  const handleCreateUser = async (e) => {
    e.preventDefault();
    try {
      await api.post('/users', newUser);
      toast.success('User created successfully!');
      setShowAddModal(false);
      setNewUser({
        full_name: '', email: '', phone_number: '',
        password: '', role: '', district_id: '',
        cnic: '', employee_id: ''
      });
      fetchUsers();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to create user');
    }
  };

  const handleView = (user) => {
    setSelectedUser(user);
    setShowViewModal(true);
  };

  const handleEdit = (user) => {
    setSelectedUser(user);
    setEditData({
      full_name: user.full_name,
      email: user.email,
      phone_number: user.phone_number,
      role: user.role,
      district_id: user.district_id || '',
      cnic: user.cnic || '',
      employee_id: user.employee_id || '',
    });
    setShowEditModal(true);
  };

  const handleUpdateUser = async (e) => {
    e.preventDefault();
    try {
      await api.put(`/users/${selectedUser.id}`, editData);
      toast.success('User updated successfully!');
      setShowEditModal(false);
      fetchUsers();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to update user');
    }
  };

  const handleDeactivate = async (id) => {
    if (!window.confirm('Are you sure you want to deactivate this user?')) return;
    try {
      await api.put(`/users/${id}/deactivate`);
      toast.success('User deactivated successfully');
      fetchUsers();
    } catch (err) {
      toast.error('Failed to deactivate user');
    }
  };

  const handleReactivate = async (id) => {
    try {
      await api.put(`/users/${id}/reactivate`);
      toast.success('User reactivated successfully');
      fetchUsers();
    } catch (err) {
      toast.error('Failed to reactivate user');
    }
  };

  const fieldUserRoles = ['Monitoring Officer', 'Driver', 'Peon'];

  const getRoleBadgeColor = (role) => {
    const colors = {
      'Super Admin': 'bg-red-100 text-red-700',
      'Secretary': 'bg-purple-100 text-purple-700',
      'Director': 'bg-purple-100 text-purple-700',
      'Deputy Director Public': 'bg-blue-100 text-blue-700',
      'Deputy Director Private': 'bg-blue-100 text-blue-700',
      'Deputy Director Procurement': 'bg-blue-100 text-blue-700',
      'Deputy Director Admin & Finance': 'bg-blue-100 text-blue-700',
      'Chairman': 'bg-red-100 text-red-700',
      'Assistant Director': 'bg-indigo-100 text-indigo-700',
      'IT Administrator': 'bg-gray-100 text-gray-700',
      'Monitoring Officer': 'bg-green-100 text-green-700',
      'Driver': 'bg-yellow-100 text-yellow-700',
      'Peon': 'bg-orange-100 text-orange-700',
    };
    return colors[role] || 'bg-gray-100 text-gray-700';
  };

  return (
    <Layout title="Users Management">

      {/* Top Bar */}
      <div className="flex justify-between items-center mb-6">
        <div className="flex gap-3">
          <div className="relative">
            <Search size={16} className="absolute left-3 top-3 text-gray-400" />
            <input
              type="text"
              placeholder="Search users..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-9 pr-4 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
          <select
            value={roleFilter}
            onChange={(e) => setRoleFilter(e.target.value)}
            className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <option value="">All Roles</option>
            <option value="Super Admin">Super Admin</option>
            <option value="Chairman">Chairman</option>
            <option value="Secretary">Secretary</option>
            <option value="Director">Director</option>
            <option value="Deputy Director Public">Deputy Director Public</option>
            <option value="Deputy Director Private">Deputy Director Private</option>
            <option value="Deputy Director Procurement">Deputy Director Procurement</option>
            <option value="Deputy Director Admin & Finance">Deputy Director Admin & Finance</option>
            <option value="Assistant Director">Assistant Director</option>
            <option value="IT Administrator">IT Administrator</option>
            <option value="Monitoring Officer">Monitoring Officer</option>
            <option value="Driver">Driver</option>
            <option value="Peon">Peon</option>
          </select>
        </div>
        <div className="flex gap-3">
          <button
            onClick={() => exportUsersReport(users)}
            className="flex items-center gap-2 bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 transition text-sm"
          >
            <Download size={16} />
            Export PDF
          </button>
          <button
            onClick={() => setShowAddModal(true)}
            className="flex items-center gap-2 bg-blue-800 text-white px-4 py-2 rounded-lg hover:bg-blue-900 transition text-sm"
          >
            <UserPlus size={16} />
            Add New User
          </button>
        </div>
      </div>

      {/* Users Table */}
      <div className="bg-white rounded-xl shadow">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-gray-50 border-b border-gray-200">
                <th className="text-left px-4 py-3 text-gray-600 font-semibold">#</th>
                <th className="text-left px-4 py-3 text-gray-600 font-semibold">Name</th>
                <th className="text-left px-4 py-3 text-gray-600 font-semibold">Email</th>
                <th className="text-left px-4 py-3 text-gray-600 font-semibold">Phone</th>
                <th className="text-left px-4 py-3 text-gray-600 font-semibold">CNIC</th>
                <th className="text-left px-4 py-3 text-gray-600 font-semibold">Employee ID</th>
                <th className="text-left px-4 py-3 text-gray-600 font-semibold">Role</th>
                <th className="text-left px-4 py-3 text-gray-600 font-semibold">District</th>
                <th className="text-center px-4 py-3 text-gray-600 font-semibold">Status</th>
                <th className="text-center px-4 py-3 text-gray-600 font-semibold">Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan="10" className="text-center py-8 text-gray-500">Loading users...</td>
                </tr>
              ) : users.length === 0 ? (
                <tr>
                  <td colSpan="10" className="text-center py-8 text-gray-500">No users found</td>
                </tr>
              ) : (
                users.map((user, index) => (
                  <tr key={user.id} className="border-t border-gray-100 hover:bg-gray-50">
                    <td className="px-4 py-3 text-gray-500">{index + 1}</td>
                    <td className="px-4 py-3 font-medium text-gray-800">{user.full_name}</td>
                    <td className="px-4 py-3 text-gray-600">{user.email}</td>
                    <td className="px-4 py-3 text-gray-600">{user.phone_number}</td>
                    <td className="px-4 py-3 text-gray-600">{user.cnic || '—'}</td>
                    <td className="px-4 py-3 text-gray-600">{user.employee_id || '—'}</td>
                    <td className="px-4 py-3">
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${getRoleBadgeColor(user.role)}`}>
                        {user.role}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-gray-600">{user.district_name || '—'}</td>
                    <td className="px-4 py-3 text-center">
                      {user.is_active ? (
                        <span className="flex items-center justify-center gap-1 text-green-600 text-xs">
                          <CheckCircle size={14} /> Active
                        </span>
                      ) : (
                        <span className="flex items-center justify-center gap-1 text-red-500 text-xs">
                          <XCircle size={14} /> Inactive
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-center">
                      <div className="flex items-center justify-center gap-2">
                        <button onClick={() => handleView(user)}
                          className="text-blue-600 hover:text-blue-800 text-xs font-medium">View</button>
                        <span className="text-gray-300">|</span>
                        <button onClick={() => handleEdit(user)}
                          className="text-green-600 hover:text-green-800 text-xs font-medium">Edit</button>
                        <span className="text-gray-300">|</span>
                        {user.is_active ? (
                          <button onClick={() => handleDeactivate(user.id)}
                            className="text-red-500 hover:text-red-700 text-xs font-medium">Deactivate</button>
                        ) : (
                          <button onClick={() => handleReactivate(user.id)}
                            className="text-green-600 hover:text-green-800 text-xs font-medium">Reactivate</button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
        <div className="px-4 py-3 border-t border-gray-100 text-sm text-gray-500">
          Total: {users.length} users
        </div>
      </div>

      {/* Add User Modal */}
      {showAddModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl shadow-xl p-6 w-full max-w-lg max-h-screen overflow-y-auto">
            <h2 className="text-lg font-bold text-gray-800 mb-4">Add New User</h2>
            <form onSubmit={handleCreateUser}>
              <div className="grid grid-cols-2 gap-3">
                <div className="col-span-2">
                  <label className="block text-xs font-medium text-gray-700 mb-1">Full Name *</label>
                  <input type="text" required value={newUser.full_name}
                    onChange={(e) => setNewUser({...newUser, full_name: e.target.value})}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">Email *</label>
                  <input type="email" required value={newUser.email}
                    onChange={(e) => setNewUser({...newUser, email: e.target.value})}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">Phone *</label>
                  <input type="text" required value={newUser.phone_number}
                    onChange={(e) => setNewUser({...newUser, phone_number: e.target.value})}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">CNIC</label>
                  <input type="text" placeholder="XXXXX-XXXXXXX-X" value={newUser.cnic}
                    onChange={(e) => setNewUser({...newUser, cnic: e.target.value})}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">Employee ID</label>
                  <input type="text" placeholder="e.g. EMP-001" value={newUser.employee_id}
                    onChange={(e) => setNewUser({...newUser, employee_id: e.target.value})}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">Password *</label>
                  <input type="password" required value={newUser.password}
                    onChange={(e) => setNewUser({...newUser, password: e.target.value})}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">Role *</label>
                  <select required value={newUser.role}
                    onChange={(e) => setNewUser({...newUser, role: e.target.value})}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500">
                    <option value="">Select Role</option>
                    <option value="Chairman">Chairman</option>
                    <option value="Secretary">Secretary</option>
                    <option value="Director">Director</option>
                    <option value="Deputy Director Public">Deputy Director Public</option>
                    <option value="Deputy Director Private">Deputy Director Private</option>
                    <option value="Deputy Director Procurement">Deputy Director Procurement</option>
                    <option value="Deputy Director Admin & Finance">Deputy Director Admin & Finance</option>
                    <option value="Assistant Director">Assistant Director</option>
                    <option value="IT Administrator">IT Administrator</option>
                    <option value="Monitoring Officer">Monitoring Officer</option>
                    <option value="Driver">Driver</option>
                    <option value="Peon">Peon</option>
                  </select>
                </div>
                {fieldUserRoles.includes(newUser.role) && (
                  <div className="col-span-2">
                    <label className="block text-xs font-medium text-gray-700 mb-1">District *</label>
                    <select required value={newUser.district_id}
                      onChange={(e) => setNewUser({...newUser, district_id: e.target.value})}
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500">
                      <option value="">Select District</option>
                      {districts.map(d => (
                        <option key={d.id} value={d.id}>{d.district_name} — {d.division_name}</option>
                      ))}
                    </select>
                  </div>
                )}
              </div>
              <div className="flex gap-3 mt-4">
                <button type="button" onClick={() => setShowAddModal(false)}
                  className="flex-1 border border-gray-300 text-gray-700 py-2 rounded-lg text-sm hover:bg-gray-50">Cancel</button>
                <button type="submit"
                  className="flex-1 bg-blue-800 text-white py-2 rounded-lg text-sm hover:bg-blue-900">Create User</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* View Modal */}
      {showViewModal && selectedUser && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-md">
            <div className="bg-blue-900 text-white p-4 rounded-t-xl flex justify-between items-center">
              <h2 className="text-lg font-bold">User Details</h2>
              <button onClick={() => setShowViewModal(false)} className="text-white text-2xl font-bold">×</button>
            </div>
            <div className="p-6 text-sm">
              <div className="grid grid-cols-2 gap-3">
                <div className="col-span-2">
                  <p className="text-gray-500 text-xs">Full Name</p>
                  <p className="font-medium">{selectedUser.full_name}</p>
                </div>
                <div>
                  <p className="text-gray-500 text-xs">Email</p>
                  <p className="font-medium">{selectedUser.email}</p>
                </div>
                <div>
                  <p className="text-gray-500 text-xs">Phone</p>
                  <p className="font-medium">{selectedUser.phone_number}</p>
                </div>
                <div>
                  <p className="text-gray-500 text-xs">CNIC</p>
                  <p className="font-medium">{selectedUser.cnic || '—'}</p>
                </div>
                <div>
                  <p className="text-gray-500 text-xs">Employee ID</p>
                  <p className="font-medium">{selectedUser.employee_id || '—'}</p>
                </div>
                <div>
                  <p className="text-gray-500 text-xs">Role</p>
                  <p className="font-medium">{selectedUser.role}</p>
                </div>
                <div>
                  <p className="text-gray-500 text-xs">District</p>
                  <p className="font-medium">{selectedUser.district_name || '—'}</p>
                </div>
                <div>
                  <p className="text-gray-500 text-xs">Status</p>
                  <p className="font-medium">{selectedUser.is_active ? '✅ Active' : '❌ Inactive'}</p>
                </div>
                <div>
                  <p className="text-gray-500 text-xs">Last Login</p>
                  <p className="font-medium">
                    {selectedUser.last_login_at
                      ? new Date(selectedUser.last_login_at).toLocaleDateString('en-PK')
                      : '—'}
                  </p>
                </div>
              </div>
              <div className="flex gap-3 mt-4">
                <button onClick={() => setShowViewModal(false)}
                  className="flex-1 border border-gray-300 text-gray-700 py-2 rounded-lg text-sm hover:bg-gray-50">Close</button>
                <button onClick={() => { setShowViewModal(false); handleEdit(selectedUser); }}
                  className="flex-1 bg-blue-800 text-white py-2 rounded-lg text-sm hover:bg-blue-900">Edit User</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Edit Modal */}
      {showEditModal && selectedUser && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-lg max-h-screen overflow-y-auto">
            <div className="bg-green-700 text-white p-4 rounded-t-xl flex justify-between items-center">
              <h2 className="text-lg font-bold">Edit User</h2>
              <button onClick={() => setShowEditModal(false)} className="text-white text-2xl font-bold">×</button>
            </div>
            <form onSubmit={handleUpdateUser} className="p-6">
              <div className="grid grid-cols-2 gap-3">
                <div className="col-span-2">
                  <label className="block text-xs font-medium text-gray-700 mb-1">Full Name</label>
                  <input type="text" required value={editData.full_name}
                    onChange={(e) => setEditData({...editData, full_name: e.target.value})}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">Email</label>
                  <input type="email" value={editData.email}
                    onChange={(e) => setEditData({...editData, email: e.target.value})}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">Phone</label>
                  <input type="text" value={editData.phone_number}
                    onChange={(e) => setEditData({...editData, phone_number: e.target.value})}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">CNIC</label>
                  <input type="text" placeholder="XXXXX-XXXXXXX-X" value={editData.cnic}
                    onChange={(e) => setEditData({...editData, cnic: e.target.value})}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">Employee ID</label>
                  <input type="text" placeholder="e.g. EMP-001" value={editData.employee_id}
                    onChange={(e) => setEditData({...editData, employee_id: e.target.value})}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">Role</label>
                  <select value={editData.role}
                    onChange={(e) => setEditData({...editData, role: e.target.value})}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500">
                    <option value="Chairman">Chairman</option>
                    <option value="Secretary">Secretary</option>
                    <option value="Director">Director</option>
                    <option value="Deputy Director Public">Deputy Director Public</option>
                    <option value="Deputy Director Private">Deputy Director Private</option>
                    <option value="Deputy Director Procurement">Deputy Director Procurement</option>
                    <option value="Deputy Director Admin & Finance">Deputy Director Admin & Finance</option>
                    <option value="Assistant Director">Assistant Director</option>
                    <option value="IT Administrator">IT Administrator</option>
                    <option value="Monitoring Officer">Monitoring Officer</option>
                    <option value="Driver">Driver</option>
                    <option value="Peon">Peon</option>
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">District</label>
                  <select value={editData.district_id}
                    onChange={(e) => setEditData({...editData, district_id: e.target.value})}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500">
                    <option value="">No District</option>
                    {districts.map(d => (
                      <option key={d.id} value={d.id}>{d.district_name} — {d.division_name}</option>
                    ))}
                  </select>
                </div>
              </div>
              <div className="flex gap-3 mt-4">
                <button type="button" onClick={() => setShowEditModal(false)}
                  className="flex-1 border border-gray-300 text-gray-700 py-2 rounded-lg text-sm hover:bg-gray-50">Cancel</button>
                <button type="submit"
                  className="flex-1 bg-green-700 text-white py-2 rounded-lg text-sm hover:bg-green-800">Save Changes</button>
              </div>
            </form>
          </div>
        </div>
      )}

    </Layout>
  );
};

export default Users;